/**
 * Hook per risolvere @NUMERO nelle menzioni WhatsApp ai nomi dei contatti.
 * Usa i partecipanti del gruppo come fonte primaria (1 sola chiamata API per gruppo).
 */

import { useState, useEffect, useRef } from 'react';
import { getWahaService } from '../../services/wahaApiService';
import { useWhatsAppChatStore } from '../../stores/whatsappChatStore';
import { debugLog } from '../../../components/whatsapp2/chat/ChatDebugPanel';

// Cache globale: groupId → Map<numero, nome>
const groupParticipantsCache = new Map<string, Map<string, string>>();
// Evita fetch concorrenti per lo stesso gruppo
const pendingGroupFetches = new Map<string, Promise<Map<string, string>>>();

/**
 * Fetcha i partecipanti di un gruppo (con cache).
 */
export async function fetchGroupParticipants(groupId: string): Promise<Map<string, string>> {
  const cached = groupParticipantsCache.get(groupId);
  if (cached) return cached;

  const pending = pendingGroupFetches.get(groupId);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const service = getWahaService();
      const participants = await service.getGroupParticipants(groupId);
      groupParticipantsCache.set(groupId, participants);
      debugLog('GroupParticipants', `✅ ${participants.size} partecipanti per ${groupId.slice(0,20)}`,
        Object.fromEntries(Array.from(participants.entries()).slice(0, 10)), 'data');
      return participants;
    } catch (err) {
      console.error('[fetchGroupParticipants] Error for group', groupId, ':', err);
      return new Map<string, string>();
    } finally {
      pendingGroupFetches.delete(groupId);
    }
  })();

  pendingGroupFetches.set(groupId, promise);
  return promise;
}

/**
 * Hook che estrae tutti i @NUMERO da un array di messaggi e ne risolve i nomi
 * usando la lista partecipanti del gruppo.
 * Ritorna una Map<numero, nome> che si aggiorna quando i nomi vengono risolti.
 */
export function useMentionNames(
  messages: Array<{ content: string }>,
  isGroup: boolean,
  chatId?: string
) {
  const [namesMap, setNamesMap] = useState<Map<string, string>>(new Map());
  const { wahaConfig } = useWhatsAppChatStore();
  const lastChatIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isGroup || !wahaConfig || !chatId || messages.length === 0) return;

    // Estrai tutti i numeri dalle menzioni
    const mentionNumbers = new Set<string>();
    for (const msg of messages) {
      const matches = msg.content.matchAll(/@(\d{5,})/g);
      for (const match of matches) {
        mentionNumbers.add(match[1]);
      }
    }

    if (mentionNumbers.size === 0) return;

    // Reset se cambio chat
    if (lastChatIdRef.current !== chatId) {
      lastChatIdRef.current = chatId;
    }

    // Controlla se la cache ha già tutti i nomi necessari
    const cached = groupParticipantsCache.get(chatId);
    if (cached) {
      const resolved = new Map<string, string>();
      for (const num of mentionNumbers) {
        const name = cached.get(num);
        if (name) resolved.set(num, name);
      }
      if (resolved.size > 0) {
        setNamesMap(resolved);
      }
      return;
    }

    // Fetch partecipanti del gruppo
    let cancelled = false;
    fetchGroupParticipants(chatId).then((participants) => {
      if (cancelled) return;
      const resolved = new Map<string, string>();
      for (const num of mentionNumbers) {
        const name = participants.get(num);
        if (name) resolved.set(num, name);
      }
      if (resolved.size > 0) {
        setNamesMap(resolved);
      }
    });

    return () => { cancelled = true; };
  }, [messages, isGroup, wahaConfig, chatId]);

  return namesMap;
}

/**
 * Hook che ritorna i nomi dei partecipanti di un gruppo (per ChatHeader subtitle).
 * Riusa la stessa cache di useMentionNames.
 */
export function useGroupParticipantNames(chatId: string | undefined, isGroup: boolean): string[] {
  const [names, setNames] = useState<string[]>([]);
  const { wahaConfig } = useWhatsAppChatStore();

  useEffect(() => {
    if (!isGroup || !chatId || !wahaConfig) return;

    let cancelled = false;
    fetchGroupParticipants(chatId).then((participants) => {
      if (cancelled) return;
      setNames(Array.from(participants.values()));
    });

    return () => { cancelled = true; };
  }, [chatId, isGroup, wahaConfig]);

  return names;
}

/**
 * Pulisce la cache dei partecipanti gruppo
 */
export function clearContactNameCache() {
  groupParticipantsCache.clear();
}
